import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, ClipboardCheck, Building2, Clock } from "lucide-react";

export default function DWR() {
  const { user } = useAuth();
  const isReviewer = user?.role === "admin" || user?.role === "hr";
  const [projects, setProjects] = useState([]);
  const [mine, setMine] = useState([]);
  const [all, setAll] = useState([]);
  const [summary, setSummary] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ date: today, project_id: "", hours: 8, tasks: "", blockers: "" });

  const load = async () => {
    const [p, m, s] = await Promise.all([
      api.get("/projects"),
      user?.employee_id ? api.get("/dwr/mine") : Promise.resolve({ data: [] }),
      api.get("/dwr/summary"),
    ]);
    setProjects(p.data); setMine(m.data); setSummary(s.data);
    if (isReviewer) setAll((await api.get("/dwr/all")).data);
  };
  useEffect(() => { load(); }, [user?.employee_id, isReviewer]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.project_id) { toast.error("Pick a project"); return; }
    setBusy(true);
    try {
      await api.post("/dwr", { ...form, hours: Number(form.hours) });
      toast.success("DWR submitted");
      setShowForm(false);
      setForm({ date: today, project_id: "", hours: 8, tasks: "", blockers: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="dwr-root">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase font-semibold tracking-[0.1em] text-slate-500">DWR & Timesheets</div>
          <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-slate-900">Daily work, delivered.</h1>
          <p className="mt-1 text-sm text-slate-500">Every engineer's day tied back to a customer and a project.</p>
        </div>
        {user?.employee_id && (
          <Button onClick={() => setShowForm(v => !v)} className="bg-[#DC2626] hover:bg-[#B91C1C]" data-testid="add-dwr-btn">
            <Plus className="h-4 w-4 mr-1.5" strokeWidth={1.8} /> {showForm ? "Close" : "Log work"}
          </Button>
        )}
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatSm label="Total hours" value={summary.total_hours} icon={Clock} />
          <StatSm label="Reports submitted" value={summary.total_reports} icon={ClipboardCheck} />
          <StatSm label="Active projects" value={projects.filter(p => p.status === "Active").length} icon={Building2} />
          <StatSm label="Top project" value={summary.by_project?.[0]?.project || "—"} sub={summary.by_project?.[0] ? `${summary.by_project[0].hours}h` : ""} icon={Building2} />
        </div>
      )}

      {showForm && (
        <Card className="p-6 rounded-xl border-slate-200/60" data-testid="dwr-form-card">
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-1.5">
              <Label>Date</Label>
              <Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} data-testid="dwr-date" />
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label>Project</Label>
              <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                <SelectTrigger data-testid="dwr-project"><SelectValue placeholder="Pick a project" /></SelectTrigger>
                <SelectContent>
                  {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.code} · {p.customer}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Hours</Label>
              <Input type="number" min="0.5" step="0.5" value={form.hours} onChange={e => setForm({ ...form, hours: e.target.value })} data-testid="dwr-hours" />
            </div>
            <div className="space-y-1.5 md:col-span-4">
              <Label>Tasks completed</Label>
              <Textarea rows={3} value={form.tasks} onChange={e => setForm({ ...form, tasks: e.target.value })} placeholder="What did you achieve today?" data-testid="dwr-tasks" />
            </div>
            <div className="space-y-1.5 md:col-span-4">
              <Label>Blockers (optional)</Label>
              <Textarea rows={2} value={form.blockers} onChange={e => setForm({ ...form, blockers: e.target.value })} placeholder="Anything blocking you?" data-testid="dwr-blockers" />
            </div>
            <div className="md:col-span-4 flex justify-end">
              <Button disabled={busy} type="submit" className="bg-[#DC2626] hover:bg-[#B91C1C]" data-testid="dwr-submit">Submit</Button>
            </div>
          </form>
        </Card>
      )}

      <Tabs defaultValue={isReviewer ? "all" : "mine"}>
        <TabsList>
          {user?.employee_id && <TabsTrigger value="mine" data-testid="tab-my-dwr">My reports</TabsTrigger>}
          {isReviewer && <TabsTrigger value="all" data-testid="tab-all-dwr">All engineers</TabsTrigger>}
          <TabsTrigger value="projects" data-testid="tab-projects">Projects</TabsTrigger>
        </TabsList>

        {user?.employee_id && (
          <TabsContent value="mine"><DWRList items={mine} /></TabsContent>
        )}
        {isReviewer && (
          <TabsContent value="all"><DWRList items={all} showName /></TabsContent>
        )}
        <TabsContent value="projects">
          <div className="grid md:grid-cols-2 gap-4">
            {projects.map(p => (
              <Card key={p.id} className="p-5 rounded-xl border-slate-200/60">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-xs font-mono text-slate-500">{p.code}</div>
                    <div className="font-semibold text-slate-900 mt-0.5">{p.name}</div>
                    <div className="text-xs text-slate-500 mt-1">Customer: {p.customer} · Manager: {p.manager || "—"}</div>
                  </div>
                  <Badge className={p.status === "Active" ? "bg-emerald-50 text-emerald-700 border-0" : "bg-slate-100 text-slate-700 border-0"}>{p.status}</Badge>
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function StatSm({ label, value, sub, icon: Icon }) {
  return (
    <Card className="p-4 rounded-xl border-slate-200/60">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase font-semibold tracking-wider text-slate-500">{label}</div>
          <div className="mt-1 font-display text-2xl font-bold text-slate-900">{value}</div>
          {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
        </div>
        <Icon className="h-5 w-5 text-[#DC2626]" strokeWidth={1.5} />
      </div>
    </Card>
  );
}

function DWRList({ items, showName }) {
  if (!items.length) return <div className="text-sm text-slate-500 py-10 text-center border border-dashed rounded-xl">No reports yet.</div>;
  return (
    <div className="space-y-3">
      {items.map(d => (
        <Card key={d.id} className="p-4 rounded-xl border-slate-200/60" data-testid={`dwr-${d.id}`}>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
                {showName && <span className="font-semibold text-slate-900">{d.employee_name}</span>}
                <span>{d.date}</span>
                <span>·</span>
                <span className="font-mono">{d.project_code}</span>
                <span>·</span>
                <span>{d.customer}</span>
              </div>
              <div className="mt-1 text-sm text-slate-800">{d.tasks}</div>
              {d.blockers && (
                <div className="mt-2 text-xs text-orange-700 bg-orange-50 rounded-md px-2 py-1 inline-block">
                  Blocker: {d.blockers}
                </div>
              )}
            </div>
            <Badge className="bg-red-50 text-[#DC2626] border-0 shrink-0">{d.hours}h</Badge>
          </div>
        </Card>
      ))}
    </div>
  );
}
